/**
 * ConfidenceBadge Component
 * Displays a confidence score as a colored badge
 */
import { cn } from '@/utils/cn';

export interface ConfidenceBadgeProps {
  confidence: number; // 0-1 range
  showLabel?: boolean;
  className?: string;
}

export function ConfidenceBadge({
  confidence,
  showLabel = true,
  className,
}: ConfidenceBadgeProps) {
  // Convert to percentage
  const percentage = Math.round(confidence * 100);

  // Determine color based on confidence level
  const { bgColor, textColor } = getConfidenceColors(confidence);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
        bgColor,
        textColor,
        className
      )}
      title={`${percentage}% confidence match`}
    >
      <ConfidenceIndicator confidence={confidence} />
      <span>{percentage}%</span>
      {showLabel && <span className="hidden sm:inline">match</span>}
    </span>
  );
}

/**
 * Small visual indicator for confidence level
 */
function ConfidenceIndicator({ confidence }: { confidence: number }) {
  const { dotColor } = getConfidenceColors(confidence);

  return (
    <span
      className={cn('w-1.5 h-1.5 rounded-full', dotColor)}
      aria-hidden="true"
    />
  );
}

/**
 * Get colors based on confidence level
 */
function getConfidenceColors(confidence: number): {
  bgColor: string;
  textColor: string;
  dotColor: string;
} {
  if (confidence >= 0.9) {
    return {
      bgColor: 'bg-green-100',
      textColor: 'text-green-700',
      dotColor: 'bg-green-500',
    };
  }

  if (confidence >= 0.7) {
    return {
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-700',
      dotColor: 'bg-yellow-500',
    };
  }

  if (confidence >= 0.5) {
    return {
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-700',
      dotColor: 'bg-orange-500',
    };
  }

  return {
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-600',
    dotColor: 'bg-gray-400',
  };
}

export default ConfidenceBadge;
